import { io } from 'socket.io-client';

import { getClientFingerprint } from './getClientFingerprint';

export class ClientLobby {
  constructor({ url, channelId, session = {} } = {}) {
    this.url =
      url ||
      process.env.SERVICE_LOBBY ||
      (typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.hostname}:4000`
        : 'http://localhost:4000');

    this.fingerprint = getClientFingerprint();

    this.channelId = channelId;
    if (!this.channelId) throw new Error('channelId es obligatorio');

    this.title = session?.firstName
      ? `${session.firstName} ${session.lastName || ''}`.trim()
      : session?.email || `Guest ${this.fingerprint}`;
    this.session = session;
    this.socket = null;
    this.chatId = this.fingerprint;
    this.callbacks = {
      message: [],
      blockState: [],
      presence: [],
      status: [],
    };
    this.aiBlocked = false;
    this.lastPresence = null;
  }

  onMessage(cb) {
    this.callbacks.message.push(cb);
    return () => this.off('message', cb);
  }

  onBlockState(cb) {
    this.callbacks.blockState.push(cb);
    return () => this.off('blockState', cb);
  }

  onPresence(cb) {
    this.callbacks.presence.push(cb);
    return () => this.off('presence', cb);
  }

  onStatus(cb) {
    this.callbacks.status.push(cb);
    return () => this.off('status', cb);
  }

  off(type, cb) {
    this.callbacks[type] = (this.callbacks[type] || []).filter((fn) => fn !== cb);
  }

  _emit(type, payload) {
    (this.callbacks[type] || []).forEach((cb) => cb(payload));
  }

  connect() {
    if (this.socket) return;
    this.socket = io(this.url.replace('/lobby', ''), {
      auth: {
        fingerprint: this.fingerprint,
        channelId: this.channelId,
        session: this.session,
      },
      path: '/lobby/socket.io',
      transports: ['websocket'],
    });
    this._emit('status', 'connecting');

    this.socket.on('connect', () => {
      this._emit('status', 'connected');
      this.socket.emit('join_chat', {
        chatId: this.chatId,
        title: this.title,
        fingerprint: this.fingerprint,
        channelId: this.channelId,
        session: this.session,
      });
    });

    this.socket.on('disconnect', () => {
      this._emit('status', 'disconnected');
    });

    this.socket.on('message', (msg) => {
      this._emit('message', { ...msg, createdAt: msg.createdAt ?? Date.now() });
    });

    this.socket.on('ai_block_state', ({ chatId, fingerprint, blocked }) => {
      if (chatId === this.chatId || fingerprint === this.fingerprint) {
        if (chatId && chatId !== this.chatId) this.chatId = chatId;
        if (this.aiBlocked !== blocked) {
          this.aiBlocked = blocked;
          this._emit('blockState', blocked);
        }
      }
    });

    this.socket.on('presence', ({ chatId, online }) => {
      if (chatId === this.chatId) {
        if (this.lastPresence !== online) {
          this.lastPresence = online;
          this._emit('presence', online);
        }
      }
    });
    this.socket.on('presence_snapshot', (snapshot = {}) => {
      const online = snapshot[this.chatId];
      if (online !== undefined && online !== this.lastPresence) {
        this.lastPresence = online;
        this._emit('presence', online);
      }
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this._emit('status', 'disconnected');
    }
  }

  requestHumanSupport() {
    if (!this.socket) return;
    this.socket.emit('request_human_support', {
      chatId: this.chatId,
      channelId: this.channelId,
      fingerprint: this.fingerprint,
    });
  }

  send({ body, author = 'user', meta }) {
    if (!this.socket) return false;
    if (author === 'ai' && this.aiBlocked) return true;
    this.socket.emit('message', {
      chatId: this.chatId,
      body,
      author,
      meta,
      session: this.session,
      channelId: this.channelId,
      fingerprint: this.fingerprint,
    });
    return true;
  }
}
